import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchEntityDocuments, type EntityDocsParams } from '../../lib/exploreApi';
import type { IocLookupDocument } from '../../lib/graphApi';
import { getActiveZoom } from '../../lib/zoomPosition';

/** Map Neo4j label → explore API category. */
const LABEL_TO_CATEGORY: Record<string, string> = {
  ThreatActor: 'actor',
  MalwareFamily: 'family',
  IOC: 'ioc',
  CVE: 'cve',
  AttackTechnique: 'technique',
};

interface DocBadgePopoverProps {
  entityName: string;
  entityLabel: string;
  docCount: number;
  anchorEl: HTMLElement;
  onClose: () => void;
}

export function DocBadgePopover({
  entityName,
  entityLabel,
  docCount,
  anchorEl,
  onClose,
}: DocBadgePopoverProps) {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<IocLookupDocument[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const category = LABEL_TO_CATEGORY[entityLabel] || 'family';
    setLoading(true);
    fetchEntityDocuments({ value: entityName, category, limit: 20 })
      .then(resp => {
        if (!cancelled) setDocs(resp.documents);
      })
      .catch(() => {
        if (!cancelled) setDocs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [entityName, entityLabel]);

  // Position relative to anchor (unscaled CSS px; see getActiveZoom)
  const z = getActiveZoom();
  const rect = anchorEl.getBoundingClientRect();
  const style: React.CSSProperties = {
    position: 'fixed',
    left: rect.right / z + 8,
    top: rect.top / z,
    zIndex: 1000,
  };

  return (
    <>
      <div className="doc-badge-popover-overlay" onClick={onClose} />
      <div className="doc-badge-popover" style={style}>
        <div className="doc-badge-popover-header">
          <span>Documents ({docCount})</span>
          <button className="doc-badge-popover-close" onClick={onClose}>x</button>
        </div>
        <div className="doc-badge-popover-body">
          {loading && <div className="doc-badge-popover-loading">Loading...</div>}
          {!loading && docs && docs.length === 0 && (
            <div className="doc-badge-popover-empty">No documents found</div>
          )}
          {!loading && docs && docs.length > 0 && (
            <ul className="doc-badge-popover-list">
              {docs.map(d => (
                <li key={d.cite_key} className="doc-badge-popover-item">
                  <a
                    href={`/sources/${encodeURIComponent(d.cite_key)}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/sources/${encodeURIComponent(d.cite_key)}`);
                      onClose();
                    }}
                    className="doc-badge-popover-link"
                  >
                    {d.title || d.cite_key}
                  </a>
                  {d.date && <span className="doc-badge-popover-date">{d.date}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
